import { useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';

import type { IStandardErrorResponse } from 'src/api/common';
import type { IShowing } from 'src/api/landlord-showings-api';
import { useNotifications } from 'src/notifications';
import { useCreateActivity } from 'src/services/api/activities';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';

type ICreateShowingResponse = {
    showing: Omit<IShowing, 'start_date'> & { start_date: string };
    has_sync_error: boolean;
};

export type ICreateShowingRequest = {
    property_id: number;
    lead_uuid: string;
    /**
     * Send the time in the browser-local timezone
     */
    start_time: string;
    /**
     * If the showing was created from a message, this is the UUID of that message
     */
    source_message_uuid: string | null;
    is_self_show: boolean;
}

const PATH = 'api/landlord/showings';

export const useCreateShowing = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { mutateAsync: createActivity } = useCreateActivity();

    return useMutation<
        ICreateShowingResponse,
        IStandardErrorResponse,
        ICreateShowingRequest
    >(
        async (payload) => {
            const res = await apiClient(PATH, {
                method: 'post',
                json: payload,
            }).json<ICreateShowingResponse>();

            return res;
        }, {
            onSuccess: (res) => {
                if (res.has_sync_error) {
                    addNotification(
                        'Tour created, but its calendar event could not be added',
                        'warning',
                    );
                } else {
                    addNotification('Tour created', 'success');
                }
                queryClient.invalidateQueries(queryKeys.showings);
                queryClient.invalidateQueries(queryKeys.achievements);
                queryClient.invalidateQueries([queryKeys.leadStageHistory, res.showing.lead_uuid]);
                createActivity({ time: dayjs().format(), type: 'tour_created' }).catch(() => {
                    console.error('Failed to create activity');
                });
            }, onError: (error) => {
                const errorMessage = error.msg || 'Failed to create showing';
                addNotification(errorMessage, 'error');
            }
        });
};
